<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractService',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const labelAlign = ref('right');
  const billingCategories = ref(null);
  const messages = ref({
    addService: {
      title: $fn.value.pgettext('Contract', 'Add Service'),
    },
    editService: {
      title: $fn.value.pgettext('Contract', 'Edit Service'),
    },
    addServiceFee: {
      title: $fn.value.pgettext('Contract', 'Add Service Fee'),
    },
    editServiceFee: {
      title: $fn.value.pgettext('Contract', 'Edit Service Fee'),
    },
  });
  const currencies = ref(null);
  const services = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));
  const b_5962862420439144_col = computed(() => false);

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    labelAlign: labelAlign.value,
    billingCategories: billingCategories.value,
    messages: messages.value,
    currencies: currencies.value,
    services: services.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5962862420439144_col: b_5962862420439144_col.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value.fetch('read_contract_service', {
      parameters: { id: props.oid },
      asResource: true,
    });
  };
  const $$load_billingCategories = () => {
    return $fn.value.fetch('get_tags', {
      parameters: { tag_type: 'billing_category' },
    });
  };
  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_services = () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_invoiceGroups = () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'labelAlign' },
      { name: 'billingCategories' },
      { name: 'messages' },
      { name: 'currencies' },
      { name: 'services' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5962862420439144_col', nodeId: '5962862420439144' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      billingCategories: $$load_billingCategories,
      currencies: $$load_currencies,
      services: $$load_services,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      formId,
      item,
      labelAlign,
      billingCategories,
      messages,
      currencies,
      services,
      invoiceGroups,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5962862420439144_col,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    if (loaded.value) {
      return item.value && item.value.isReadonly;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.contract ? item.value.contract.contract_number : null,
          item.value.snapshot ? item.value.snapshot('name') : null,
        ],
        icon: $config.value.contract.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ja7blc644_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_closeModal_l14ds6wig_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_7789867021953041_crud_34q1115tw_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_service',
      op: 'upsert',
      data: item.value,
      options: {
        parameters: {
          contract_id: item.value.contract.id,
        },
      },
    };
  };

  const act_7315092382398562_closeModal_ja7blc644 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ja7blc644_getActionArgs,
    });
  const inst_evh_7315092382398562_close_pb3mq20r9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ja7blc644,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_pb3mq20r9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_85muvrkld =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_wcai93mqz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_85muvrkld,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_wcai93mqz],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_h2mlqgpuq =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_ud7js5euz =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_h2mlqgpuq,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_ud7js5euz],
    event: 'close',
  });
  const act_7789867021953041_closeModal_l14ds6wig =
    new actionTypes.CloseModalAction({
      actionArgs: act_7789867021953041_closeModal_l14ds6wig_getActionArgs,
    });
  const inst_evh_7789867021953041_success_vrydwyzxu =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_closeModal_l14ds6wig,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_7789867021953041_crud_34q1115tw = new actionTypes.CRUDAction({
    actionArgs: act_7789867021953041_crud_34q1115tw_getActionArgs,
    displayName: 'save',
    events: [inst_evh_7789867021953041_success_vrydwyzxu],
  });
  const inst_evh_7789867021953041_submit_5ah5xmm75 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_crud_34q1115tw,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_7789867021953041_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7789867021953041_submit_5ah5xmm75],
    event: 'submit',
  });
  const act_2140198529698299_closeComponent_fmt2olafi =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_yg73qcqaw =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_fmt2olafi,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_yg73qcqaw],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_pb3mq20r9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_wcai93mqz,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_ud7js5euz,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_success_vrydwyzxu,
    inst_evh_7789867021953041_submit_5ah5xmm75,
    save: inst_evh_7789867021953041_submit_5ah5xmm75,
    inst_evh_7789867021953041_submit,
    inst_evh_2140198529698299_clickPreventStop_yg73qcqaw,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_pb3mq20r9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_pb3mq20r9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_wcai93mqz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_wcai93mqz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_ud7js5euz = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_ud7js5euz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_vrydwyzxu = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_vrydwyzxu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_submit_5ah5xmm75 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_submit_5ah5xmm75.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_submit = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_yg73qcqaw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_yg73qcqaw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractService"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="pgettext('ResourceTitle', 'Contract Service')"
        :resourceKey="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody
      :card="!modal"
      :modal="modal"
      no-padding
      :col="b_5962862420439144_col"
      v-if="loaded"
    >
      <BTabs
        nav-class="material px-3"
        navWrapperClass="position-sticky top-0 bg-white z-1"
        class="tabs-material"
      >
        <BTab
          :title="pgettext('ContractService', 'General')"
          class="container-fluid pt-4 px-5"
        >
          <ItpForm
            :id="formId"
            name="contract"
            class="row"
            ref="mainForm"
            @submit="evh_7789867021953041_submit($event, {})"
          >
            <ItpCol>
              <FieldsetContractService
                :item="item"
                :readonly="isReadonly"
                :labelAlign="labelAlign"
              >
              </FieldsetContractService>
              <div class="offset-md-3">
                <ItpButton
                  :text="pgettext('Button', 'Save')"
                  type="submit"
                  :form="formId"
                  variant="primary"
                  icon="fa-save"
                  :spinning="actions.save.isRunning"
                >
                </ItpButton>
              </div>
            </ItpCol>
          </ItpForm>
        </BTab>
        <BTab
          :title="pgettext('ContractService', 'Fees')"
          class="container-fluid pt-4 px-5"
        >
          <UCContractServiceFeesEditor
            :service="item"
            :readonly="isReadonly"
            :labelAlign="labelAlign"
          >
          </UCContractServiceFeesEditor>
        </BTab>
      </BTabs>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
